import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  ProjectDrawing,
  DrawingQuery,
  DrawingUploadData,
  DrawingComment,
  DrawingApprovalWorkflow,
  DrawingVersionHistory,
  CreateDrawingCommentData,
  CreateDrawingCommentReplyData,
  ApprovalActionData,
  CreateDrawingVersionData,
} from '../../types';
import drawingService from '../../services/drawingService';

// State interfaces
export interface DrawingFilters {
  search: string;
  category: string;
  status: string;
  discipline: string;
  phase: string;
  visibility: string;
  tags: string[];
  location: string;
  dateFrom: string;
  dateTo: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface DrawingsState {
  // Data
  drawings: ProjectDrawing[];
  currentDrawing: ProjectDrawing | null;
  comments: DrawingComment[];
  approvalWorkflow: DrawingApprovalWorkflow | null;
  versionHistory: DrawingVersionHistory[];

  // Configuration data
  categories: Array<{ value: string; label: string; color: string }>;
  statuses: Array<{ value: string; label: string; color: string }>;
  disciplines: Array<{ value: string; label: string }>;
  phases: Array<{ value: string; label: string }>;
  sheetSizes: Array<{ value: string; label: string }>;
  visibilityOptions: Array<{ value: string; label: string }>;
  sortOptions: Array<{ value: string; label: string }>;
  projects: Array<{ value: string; label: string }>;

  // Pagination & Filters
  filters: DrawingFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Loading states
  loading: {
    drawings: boolean;
    currentDrawing: boolean;
    uploading: boolean;
    updating: boolean;
    deleting: boolean;
    comments: boolean;
    workflow: boolean;
    versions: boolean;
    configurations: boolean;
  };

  // Error states
  errors: {
    drawings: string | null;
    currentDrawing: string | null;
    uploading: string | null;
    updating: string | null;
    deleting: string | null;
    comments: string | null;
    workflow: string | null;
    versions: string | null;
    configurations: string | null;
  };

  // UI states
  ui: {
    selectedDrawings: string[];
    viewMode: 'grid' | 'list';
    showUploadModal: boolean;
    showViewer: boolean;
    showApprovalWorkflow: boolean;
    showVersionControl: boolean;
  };
}

const initialFilters: DrawingFilters = {
  search: '',
  category: '',
  status: '',
  discipline: '',
  phase: '',
  visibility: '',
  tags: [],
  location: '',
  dateFrom: '',
  dateTo: '',
  sortBy: 'uploaded_at',
  sortOrder: 'desc',
};

const initialState: DrawingsState = {
  drawings: [],
  currentDrawing: null,
  comments: [],
  approvalWorkflow: null,
  versionHistory: [],

  categories: [],
  statuses: [],
  disciplines: [],
  phases: [],
  sheetSizes: [],
  visibilityOptions: [],
  sortOptions: [],
  projects: [],

  filters: initialFilters,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },

  loading: {
    drawings: false,
    currentDrawing: false,
    uploading: false,
    updating: false,
    deleting: false,
    comments: false,
    workflow: false,
    versions: false,
    configurations: false,
  },

  errors: {
    drawings: null,
    currentDrawing: null,
    uploading: null,
    updating: null,
    deleting: null,
    comments: null,
    workflow: null,
    versions: null,
    configurations: null,
  },

  ui: {
    selectedDrawings: [],
    viewMode: 'grid',
    showUploadModal: false,
    showViewer: false,
    showApprovalWorkflow: false,
    showVersionControl: false,
  },
};

// Async thunks
export const fetchDrawings = createAsyncThunk(
  'drawings/fetchDrawings',
  async (params: { projectId?: string; query?: DrawingQuery }) => {
    const response = await drawingService.getDrawings(params.query || {}, params.projectId);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch drawings');
    }

    return {
      drawings: response.drawings,
      pagination: {
        page: response.page,
        totalPages: response.totalPages,
        total: response.total,
      },
    };
  }
);

export const fetchDrawing = createAsyncThunk(
  'drawings/fetchDrawing',
  async (params: { drawingId: string; projectId?: string }) => {
    const response = await drawingService.getDrawing(params.drawingId, params.projectId);
    return response;
  }
);

export const uploadDrawing = createAsyncThunk(
  'drawings/uploadDrawing',
  async (params: {
    file: File;
    data: DrawingUploadData;
    projectId?: string;
    onProgress?: (progress: number) => void;
  }) => {
    const response = await drawingService.uploadDrawing(
      params.file,
      params.data,
      params.onProgress,
      params.projectId
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to upload drawing');
    }

    return response.data;
  }
);

export const updateDrawing = createAsyncThunk(
  'drawings/updateDrawing',
  async (params: {
    drawingId: string;
    data: DrawingUploadData;
    file?: File | null;
    projectId?: string;
    onProgress?: (progress: number) => void;
  }) => {
    const response = await drawingService.updateDrawing(
      params.drawingId,
      params.data,
      params.file,
      params.onProgress,
      params.projectId
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update drawing');
    }

    return response.data;
  }
);

export const deleteDrawing = createAsyncThunk(
  'drawings/deleteDrawing',
  async (params: { drawingId: string; projectId?: string }) => {
    const response = await drawingService.deleteDrawing(params.drawingId, params.projectId);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete drawing');
    }

    return params.drawingId;
  }
);

export const fetchDrawingComments = createAsyncThunk(
  'drawings/fetchComments',
  async (params: { drawingId: string; projectId?: string }) => {
    const response = await drawingService.getDrawingComments(params.drawingId, params.projectId);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch comments');
    }

    return response.data || [];
  }
);

export const createDrawingComment = createAsyncThunk(
  'drawings/createComment',
  async (params: { data: CreateDrawingCommentData; projectId?: string }) => {
    const response = await drawingService.createDrawingComment(params.data, params.projectId);

    if (!response.success) {
      throw new Error(response.message || 'Failed to create comment');
    }

    return response.data;
  }
);

export const replyToComment = createAsyncThunk(
  'drawings/replyToComment',
  async (params: { data: CreateDrawingCommentReplyData; projectId?: string }) => {
    const response = await drawingService.replyToDrawingComment(params.data, params.projectId);

    if (!response.success) {
      throw new Error(response.message || 'Failed to reply to comment');
    }

    return response.data;
  }
);

export const fetchApprovalWorkflow = createAsyncThunk(
  'drawings/fetchApprovalWorkflow',
  async (params: { drawingId: string; projectId?: string }) => {
    const response = await drawingService.getDrawingApprovalWorkflow(
      params.drawingId,
      params.projectId
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch approval workflow');
    }

    return response.data;
  }
);

export const startApprovalWorkflow = createAsyncThunk(
  'drawings/startApprovalWorkflow',
  async (params: { drawingId: string; workflowType: string; projectId?: string }) => {
    const response = await drawingService.startDrawingApproval(
      params.drawingId,
      params.workflowType,
      params.projectId
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to start approval workflow');
    }

    return response.data;
  }
);

export const submitApprovalAction = createAsyncThunk(
  'drawings/submitApprovalAction',
  async (params: { drawingId: string; data: ApprovalActionData; projectId?: string }) => {
    const response = await drawingService.submitApprovalAction(
      params.drawingId,
      params.data,
      params.projectId
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to submit approval action');
    }

    return response.data;
  }
);

export const fetchVersionHistory = createAsyncThunk(
  'drawings/fetchVersionHistory',
  async (params: { drawingId: string; projectId?: string }) => {
    const response = await drawingService.getDrawingVersionHistory(
      params.drawingId,
      params.projectId
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch version history');
    }

    return response.data || [];
  }
);

export const uploadDrawingVersion = createAsyncThunk(
  'drawings/uploadVersion',
  async (params: {
    data: CreateDrawingVersionData;
    projectId?: string;
    onProgress?: (progress: number) => void;
  }) => {
    const response = await drawingService.uploadDrawingVersion(
      params.data,
      params.onProgress,
      params.projectId
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to upload new version');
    }

    return response.data;
  }
);

export const fetchDrawingConfigurations = createAsyncThunk(
  'drawings/fetchConfigurations',
  async (showProject: boolean = false) => {
    const [configs, projects] = await Promise.all([
      drawingService.getDrawingConfigurations(),
      showProject ? drawingService.getProjectsForDropdown() : Promise.resolve([]),
    ]);

    return {
      categories: configs.categories,
      statuses: configs.statuses,
      disciplines: configs.disciplines,
      phases: configs.phases,
      sheetSizes: configs.sheetSizes,
      visibilityOptions: configs.visibilityOptions,
      sortOptions: configs.sortOptions,
      projects,
    };
  }
);

// Slice
const drawingsSlice = createSlice({
  name: 'drawings',
  initialState,
  reducers: {
    // Filter actions
    setFilters: (state, action: PayloadAction<Partial<DrawingFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },

    clearFilters: state => {
      state.filters = initialFilters;
      state.pagination.page = 1;
    },

    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.page = 1;
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },

    // Selection actions
    selectDrawing: (state, action: PayloadAction<string>) => {
      if (!state.ui.selectedDrawings.includes(action.payload)) {
        state.ui.selectedDrawings.push(action.payload);
      }
    },

    deselectDrawing: (state, action: PayloadAction<string>) => {
      state.ui.selectedDrawings = state.ui.selectedDrawings.filter(id => id !== action.payload);
    },

    selectAllDrawings: state => {
      state.ui.selectedDrawings = state.drawings.map(d => d._id);
    },

    deselectAllDrawings: state => {
      state.ui.selectedDrawings = [];
    },

    // UI actions
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.ui.viewMode = action.payload;
    },

    setCurrentDrawing: (state, action: PayloadAction<ProjectDrawing | null>) => {
      state.currentDrawing = action.payload;
    },

    // Modal actions
    showUploadModal: state => {
      state.ui.showUploadModal = true;
    },

    hideUploadModal: state => {
      state.ui.showUploadModal = false;
    },

    showViewer: (state, action: PayloadAction<ProjectDrawing>) => {
      state.currentDrawing = action.payload;
      state.ui.showViewer = true;
    },

    hideViewer: state => {
      state.ui.showViewer = false;
      state.currentDrawing = null;
    },

    // Error actions
    clearError: (state, action: PayloadAction<keyof DrawingsState['errors']>) => {
      state.errors[action.payload] = null;
    },

    clearAllErrors: state => {
      Object.keys(state.errors).forEach(key => {
        state.errors[key as keyof DrawingsState['errors']] = null;
      });
    },
  },

  extraReducers: builder => {
    // Fetch drawings
    builder
      .addCase(fetchDrawings.pending, state => {
        state.loading.drawings = true;
        state.errors.drawings = null;
      })
      .addCase(fetchDrawings.fulfilled, (state, action) => {
        state.loading.drawings = false;
        state.drawings = action.payload.drawings;
        state.pagination = {
          ...state.pagination,
          ...action.payload.pagination,
        };
      })
      .addCase(fetchDrawings.rejected, (state, action) => {
        state.loading.drawings = false;
        state.errors.drawings = action.error.message || 'Failed to fetch drawings';
      });

    // Fetch single drawing
    builder
      .addCase(fetchDrawing.pending, state => {
        state.loading.currentDrawing = true;
        state.errors.currentDrawing = null;
      })
      .addCase(fetchDrawing.fulfilled, (state, action) => {
        state.loading.currentDrawing = false;
        state.currentDrawing = action.payload;
      })
      .addCase(fetchDrawing.rejected, (state, action) => {
        state.loading.currentDrawing = false;
        state.errors.currentDrawing = action.error.message || 'Failed to fetch drawing';
      });

    // Upload drawing
    builder
      .addCase(uploadDrawing.pending, state => {
        state.loading.uploading = true;
        state.errors.uploading = null;
      })
      .addCase(uploadDrawing.fulfilled, (state, action) => {
        state.loading.uploading = false;
        state.drawings.unshift(action.payload);
        state.pagination.total += 1;
        state.ui.showUploadModal = false;
      })
      .addCase(uploadDrawing.rejected, (state, action) => {
        state.loading.uploading = false;
        state.errors.uploading = action.error.message || 'Failed to upload drawing';
      });

    // Update drawing
    builder
      .addCase(updateDrawing.pending, state => {
        state.loading.updating = true;
        state.errors.updating = null;
      })
      .addCase(updateDrawing.fulfilled, (state, action) => {
        state.loading.updating = false;
        const index = state.drawings.findIndex(d => d._id === action.payload._id);
        if (index !== -1) {
          state.drawings[index] = action.payload;
        }
        if (state.currentDrawing?._id === action.payload._id) {
          state.currentDrawing = action.payload;
        }
      })
      .addCase(updateDrawing.rejected, (state, action) => {
        state.loading.updating = false;
        state.errors.updating = action.error.message || 'Failed to update drawing';
      });

    // Delete drawing
    builder
      .addCase(deleteDrawing.pending, state => {
        state.loading.deleting = true;
        state.errors.deleting = null;
      })
      .addCase(deleteDrawing.fulfilled, (state, action) => {
        state.loading.deleting = false;
        state.drawings = state.drawings.filter(d => d._id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.ui.selectedDrawings = state.ui.selectedDrawings.filter(id => id !== action.payload);
      })
      .addCase(deleteDrawing.rejected, (state, action) => {
        state.loading.deleting = false;
        state.errors.deleting = action.error.message || 'Failed to delete drawing';
      });

    // Fetch comments
    builder
      .addCase(fetchDrawingComments.pending, state => {
        state.loading.comments = true;
        state.errors.comments = null;
      })
      .addCase(fetchDrawingComments.fulfilled, (state, action) => {
        state.loading.comments = false;
        state.comments = action.payload;
      })
      .addCase(fetchDrawingComments.rejected, (state, action) => {
        state.loading.comments = false;
        state.errors.comments = action.error.message || 'Failed to fetch comments';
      });

    // Create comment
    builder.addCase(createDrawingComment.fulfilled, (state, action) => {
      state.comments.push(action.payload);
    });

    // Fetch approval workflow
    builder
      .addCase(fetchApprovalWorkflow.pending, state => {
        state.loading.workflow = true;
        state.errors.workflow = null;
      })
      .addCase(fetchApprovalWorkflow.fulfilled, (state, action) => {
        state.loading.workflow = false;
        state.approvalWorkflow = action.payload;
      })
      .addCase(fetchApprovalWorkflow.rejected, (state, action) => {
        state.loading.workflow = false;
        state.errors.workflow = action.error.message || 'Failed to fetch workflow';
      });

    // Start approval workflow
    builder.addCase(startApprovalWorkflow.fulfilled, (state, action) => {
      state.approvalWorkflow = action.payload;
    });

    // Submit approval action
    builder.addCase(submitApprovalAction.fulfilled, (state, action) => {
      state.approvalWorkflow = action.payload;
    });

    // Fetch version history
    builder
      .addCase(fetchVersionHistory.pending, state => {
        state.loading.versions = true;
        state.errors.versions = null;
      })
      .addCase(fetchVersionHistory.fulfilled, (state, action) => {
        state.loading.versions = false;
        state.versionHistory = action.payload;
      })
      .addCase(fetchVersionHistory.rejected, (state, action) => {
        state.loading.versions = false;
        state.errors.versions = action.error.message || 'Failed to fetch version history';
      });

    // Upload version
    builder.addCase(uploadDrawingVersion.fulfilled, (state, action) => {
      state.versionHistory.unshift(action.payload);
    });

    // Fetch configurations
    builder
      .addCase(fetchDrawingConfigurations.pending, state => {
        state.loading.configurations = true;
        state.errors.configurations = null;
      })
      .addCase(fetchDrawingConfigurations.fulfilled, (state, action) => {
        state.loading.configurations = false;
        state.categories = action.payload.categories;
        state.statuses = action.payload.statuses;
        state.disciplines = action.payload.disciplines;
        state.phases = action.payload.phases;
        state.sheetSizes = action.payload.sheetSizes;
        state.visibilityOptions = action.payload.visibilityOptions;
        state.sortOptions = action.payload.sortOptions;
        state.projects = action.payload.projects;
      })
      .addCase(fetchDrawingConfigurations.rejected, (state, action) => {
        state.loading.configurations = false;
        state.errors.configurations = action.error.message || 'Failed to fetch configurations';
      });
  },
});

// Actions
export const {
  setFilters,
  clearFilters,
  setSearch,
  setPage,
  setPageSize,
  selectDrawing,
  deselectDrawing,
  selectAllDrawings,
  deselectAllDrawings,
  setViewMode,
  setCurrentDrawing,
  showUploadModal,
  hideUploadModal,
  showViewer,
  hideViewer,
  clearError,
  clearAllErrors,
} = drawingsSlice.actions;

// Selectors
export const selectDrawings = (state: { drawings: DrawingsState }) => state.drawings.drawings;
export const selectCurrentDrawing = (state: { drawings: DrawingsState }) =>
  state.drawings.currentDrawing;
export const selectDrawingComments = (state: { drawings: DrawingsState }) =>
  state.drawings.comments;
export const selectApprovalWorkflow = (state: { drawings: DrawingsState }) =>
  state.drawings.approvalWorkflow;
export const selectVersionHistory = (state: { drawings: DrawingsState }) =>
  state.drawings.versionHistory;
export const selectDrawingFilters = (state: { drawings: DrawingsState }) => state.drawings.filters;
export const selectDrawingPagination = (state: { drawings: DrawingsState }) =>
  state.drawings.pagination;
export const selectDrawingLoading = (state: { drawings: DrawingsState }) => state.drawings.loading;
export const selectDrawingErrors = (state: { drawings: DrawingsState }) => state.drawings.errors;
export const selectDrawingUI = (state: { drawings: DrawingsState }) => state.drawings.ui;
export const selectDrawingConfigurations = (state: { drawings: DrawingsState }) => ({
  categories: state.drawings.categories,
  statuses: state.drawings.statuses,
  disciplines: state.drawings.disciplines,
  phases: state.drawings.phases,
  sheetSizes: state.drawings.sheetSizes,
  visibilityOptions: state.drawings.visibilityOptions,
  sortOptions: state.drawings.sortOptions,
  projects: state.drawings.projects,
});

export default drawingsSlice.reducer;
